import { useCallback } from "react";

import isNil from "lodash/isNil";

import { DateFormat } from "@/constants/DateFormat";
import { RecurrenceValue } from "@/constants/Schedule";
import { TextTestID } from "@/constants/TestID";
import EventDetailsLines from "@/ui/components/schedule/details/EventDetailsLines";
import { Text } from "@/ui/core";
import type { StackProps } from "@/ui/layout";
import { VStack } from "@/ui/layout";
import { addSessionSuffixIfMissing } from "@/utils/booking.util";
import { formatISODate, getTimeRange } from "@/utils/date.util";
import { doesRecur, getRecurrenceString } from "@/utils/schedule.util";
import { joinTestIDs } from "@/utils/string.util";

interface AppointmentOverviewHeaderProps extends ViewWithTestID {
  appointmentTitle?: string;
  attendeeCount: number;
  paddingBottom?: StackProps["paddingBottom"];
  recurrence?: EventRecurrence;
  timeRange: BookingTimeRange;
}
const AppointmentOverviewHeader = ({
  appointmentTitle,
  attendeeCount,
  paddingBottom,
  recurrence,
  testID,
  timeRange,
}: AppointmentOverviewHeaderProps) => {
  const isRecurring = doesRecur(recurrence);

  const getRecurrenceStringValue = useCallback(() => {
    if (
      isNil(timeRange) ||
      isNil(recurrence) ||
      recurrence === RecurrenceValue.DoesNotRepeat
    ) {
      return undefined;
    }

    return getRecurrenceString({
      frequency: recurrence,
      startDate: timeRange.startTime,
    });
  }, [recurrence, timeRange]);

  return (
    <VStack gap={24} paddingBottom={paddingBottom}>
      <Text testID={joinTestIDs(testID, TextTestID.Title)} variant="h4">
        {addSessionSuffixIfMissing(appointmentTitle)}
      </Text>
      <EventDetailsLines
        isRecurring={isRecurring}
        recurrenceString={getRecurrenceStringValue()}
        startDate={formatISODate({
          dateString: timeRange.startTime,
          format: DateFormat.weekDayMonthDay,
        })}
        testID={testID}
        timeRange={getTimeRange({
          endDate: timeRange.endTime,
          startDate: timeRange.startTime,
        })}
      />
      <Text
        testID={joinTestIDs(testID, TextTestID.Title)}
        variant="captionMedium"
      >
        {attendeeCount} attendees
      </Text>
    </VStack>
  );
};

export type { AppointmentOverviewHeaderProps };
export default AppointmentOverviewHeader;
